// Tiny WebAudio synth so we don't ship (or license) any real Windows sounds.
// Browsers only allow audio after a user gesture, so these are called from click handlers.

const MUTE_KEY = "desktop-muted";

let ctx: AudioContext | null = null;

const getContext = () => {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
  }
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
};

export const isMuted = () => {
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
};

export const setMuted = (muted: boolean) => {
  try {
    localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
  } catch {
    // storage unavailable, mute just won't persist
  }
};

const tone = (
  audio: AudioContext,
  freq: number,
  start: number,
  duration: number,
  volume = 0.08,
  type: OscillatorType = "sine"
) => {
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(volume, start + 0.04);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  osc.connect(gain).connect(audio.destination);
  osc.start(start);
  osc.stop(start + duration + 0.05);
};

// Warm, rising pad chord, loosely in the spirit of a 90s OS startup
export const playStartupChime = () => {
  if (isMuted()) return;
  const audio = getContext();
  if (!audio) return;
  const t = audio.currentTime + 0.05;
  const notes = [261.63, 329.63, 392.0, 523.25, 659.25];
  notes.forEach((freq, i) => {
    tone(audio, freq, t + i * 0.12, 2.6 - i * 0.2, 0.06, "triangle");
    tone(audio, freq / 2, t + i * 0.12, 2.2, 0.025, "sine");
  });
};

export const playShutdownChime = () => {
  if (isMuted()) return;
  const audio = getContext();
  if (!audio) return;
  const t = audio.currentTime + 0.05;
  [659.25, 523.25, 392.0, 261.63].forEach((freq, i) => {
    tone(audio, freq, t + i * 0.15, 1.2, 0.06, "triangle");
  });
};

export const playClick = () => {
  if (isMuted()) return;
  const audio = getContext();
  if (!audio) return;
  tone(audio, 1800, audio.currentTime, 0.03, 0.03, "square");
};

export const playError = () => {
  if (isMuted()) return;
  const audio = getContext();
  if (!audio) return;
  const t = audio.currentTime;
  tone(audio, 440, t, 0.18, 0.06, "square");
  tone(audio, 330, t + 0.12, 0.25, 0.06, "square");
};
